import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { API_ENDPOINTS } from "@/hooks/auth-constants";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import {
  applyThemeToDocument,
  DEFAULT_THEME_PREFERENCE,
  normalizeThemePreference,
  readThemePreferenceFromStorage,
  resolveTheme,
  THEME_SETTINGS_KEY,
  writeThemePreferenceToStorage,
  type ThemePreference,
} from "@/lib/theme-preference";
import { useToast } from "@/hooks/use-toast";

type ThemePreferenceContextValue = {
  preference: ThemePreference;
  resolved: "light" | "dark";
  setPreference: (next: ThemePreference) => Promise<void>;
};

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | null>(null);

function getInitialPreference(): ThemePreference {
  return readThemePreferenceFromStorage() ?? DEFAULT_THEME_PREFERENCE;
}

export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isValidating } = useAuth();
  const { toast } = useToast();
  const [preference, setPreferenceState] = useState<ThemePreference>(getInitialPreference);

  const resolved = useMemo(() => resolveTheme(preference), [preference]);

  useEffect(() => {
    applyThemeToDocument(resolved);
  }, [resolved]);

  useEffect(() => {
    if (preference !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      applyThemeToDocument(resolveTheme("auto"));
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  useEffect(() => {
    if (!isAuthenticated || isValidating) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(API_ENDPOINTS.PROFILE_SETTINGS, { credentials: "include" });
        if (!res.ok) return;
        const body = (await res.json()) as { success?: boolean; data?: Record<string, unknown> };
        const data = body.data;
        if (!data || !(THEME_SETTINGS_KEY in data)) return;
        const next = normalizeThemePreference(data[THEME_SETTINGS_KEY]);
        if (!cancelled) {
          setPreferenceState(next);
          writeThemePreferenceToStorage(next);
          applyThemeToDocument(resolveTheme(next));
        }
      } catch {
        /* keep local preference */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isValidating]);

  const setPreference = useCallback(
    async (next: ThemePreference) => {
      const previous = preference;
      setPreferenceState(next);
      writeThemePreferenceToStorage(next);
      applyThemeToDocument(resolveTheme(next));

      if (!isAuthenticated) return;

      try {
        await apiRequest("PATCH", API_ENDPOINTS.PROFILE_SETTINGS, { [THEME_SETTINGS_KEY]: next });
      } catch {
        setPreferenceState(previous);
        writeThemePreferenceToStorage(previous);
        applyThemeToDocument(resolveTheme(previous));
        toast({
          title: "Could not save appearance",
          description: "Your choice was reverted. Check your connection and try again.",
          variant: "destructive",
        });
      }
    },
    [isAuthenticated, preference, toast],
  );

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return (
    <ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>
  );
}

export function useThemePreference(): ThemePreferenceContextValue {
  const ctx = useContext(ThemePreferenceContext);
  if (!ctx) {
    throw new Error("useThemePreference must be used within ThemePreferenceProvider");
  }
  return ctx;
}

/** Returns null when the app is not wrapped with ThemePreferenceProvider. */
export function useThemePreferenceOptional(): ThemePreferenceContextValue | null {
  return useContext(ThemePreferenceContext);
}
